import { GuaranteeSecurityType, type TGuaranteesApplication } from '~/store/guarantees/types.ts';
import {
  getSelectedGuaranteeSecurityType,
  validateGuaranteesSecretSectionForm,
} from '../../rootSection/utils.ts';
import { GuaranteesAccountTargetField, type TGuaranteesRootSectionFormValues } from '../../rootSection/types.ts';
import type { TGuaranteesStoredAccountSelection } from '../../rootSection/utils.ts';
import {
  getApplicationSecurityStringValue,
  getApplicationSecurityType,
} from './applicationIdentity.ts';

type TBuildApplicationSecurityCheckParams = {
  application: TGuaranteesApplication | null;
  defaultValues: TGuaranteesRootSectionFormValues;
  storedAccountSelection: TGuaranteesStoredAccountSelection | null;
  values: TGuaranteesRootSectionFormValues;
};

export const buildApplicationSecurityCheckState = ({
  application,
  defaultValues,
  storedAccountSelection,
  values,
}: TBuildApplicationSecurityCheckParams) => {
  const selectedSecurityType =
    getSelectedGuaranteeSecurityType(values.securityType) ||
    getApplicationSecurityType(application?.security);
  const savedSecurityCurrencies = application?.security?.currencies ?? [];
  const currentSecurityCurrencies = values.securityCurrencies.map((option) => option.value);
  const storedCoverageAccountIban =
    storedAccountSelection?.targetField === GuaranteesAccountTargetField.CoverageAccountIban
      ? storedAccountSelection.iban
      : '';
  const storedDepositAccountIban =
    storedAccountSelection?.targetField === GuaranteesAccountTargetField.DepositAccountIban
      ? storedAccountSelection.iban
      : '';
  const applicationCoverageAccountIban = getApplicationSecurityStringValue(
    application?.security,
    'coverageAccountIban'
  );
  const applicationDepositAccountIban = getApplicationSecurityStringValue(
    application?.security,
    'depositAccountIban'
  );
  const savedCoverageAccountIban = applicationCoverageAccountIban || storedCoverageAccountIban;
  const savedDepositAccountIban = applicationDepositAccountIban || storedDepositAccountIban;
  const isApplicationSecurityIbanMissing =
    (selectedSecurityType === GuaranteeSecurityType.COVERAGE_ACCOUNT &&
      !applicationCoverageAccountIban) ||
    (selectedSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS &&
      !applicationDepositAccountIban);
  const hasSecurityDraftChanges =
    isApplicationSecurityIbanMissing ||
    selectedSecurityType !== getApplicationSecurityType(application?.security) ||
    values.securityAmount !== defaultValues.securityAmount ||
    values.requiredSecurityAmount !== defaultValues.requiredSecurityAmount ||
    values.pledgorName !== defaultValues.pledgorName ||
    values.pledgorHasChangesInConstituentDocuments?.value !==
      defaultValues.pledgorHasChangesInConstituentDocuments?.value ||
    values.pledgorIdentifyCode !== defaultValues.pledgorIdentifyCode ||
    values.principalDebitAccountIban !== defaultValues.principalDebitAccountIban ||
    values.principalDebitAccount?.value !== defaultValues.principalDebitAccount?.value ||
    values.isCounterpartyPrincipalDebitAccount !==
      defaultValues.isCounterpartyPrincipalDebitAccount ||
    currentSecurityCurrencies.join('|') !== savedSecurityCurrencies.join('|') ||
    values.coverageAccountIban !== defaultValues.coverageAccountIban ||
    values.depositAccountIban !== defaultValues.depositAccountIban ||
    values.account?.value !== defaultValues.account?.value;
  const shouldValidateSecretBeforeCheck =
    !!selectedSecurityType &&
    (hasSecurityDraftChanges ||
      (selectedSecurityType === GuaranteeSecurityType.COVERAGE_ACCOUNT &&
        !savedCoverageAccountIban) ||
      (selectedSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS && !savedDepositAccountIban));

  return {
    hasSecurityDraftChanges,
    selectedSecurityType,
    shouldValidateSecretBeforeCheck,
  };
};

export const getGuaranteesSecretValidationError = (
  values: TGuaranteesRootSectionFormValues
): string => {
  const errors = validateGuaranteesSecretSectionForm(values);

  return Object.values(errors).find(Boolean) || '';
};
